import { SERVICE_NAME } from "@/config/serviceNames";
import { requests } from ".";
import { EntityType, Module } from "@/constants";

interface LabFeature {
  key: string;
  name: string;
  type: "company" | "location";
  position: number;
  details: {
    heading: string;
    body: string;
  };
  liveAt: string;
  visibleAt: string;
  enabledAt: string;
  default: boolean;
  toggleHidden: boolean;
  isIrreversibleFeature: boolean;
  isHiddenFromUi: boolean;
  changelog?: string;
  supportDoc?: string;
  media?: string[];
}

const LabsService = {
  getLabsFeatures: (type: "location" | "company") =>
    requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: EntityType.OTHERS,
      })
      .get("features", { params: { type } }),

  createLabsFeature: (body: LabFeature) =>
    requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: EntityType.OTHERS,
      })
      .post("features", body),

  updateLabsFeature: (id: string, body: Partial<LabFeature>) =>
    requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: id,
      })
      .put(`features/${id}`, body),

  deleteLabsFeature: (id: string) =>
    requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: id,
      })
      .delete(`features/${id}`),

  listFeatureFeedbacks: (params: {
    featureKey?: string;
    skip?: number;
    limit?: string;
  }) =>
    requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: params?.featureKey || EntityType.OTHERS,
      })
      .get("feedbacks", { params }),

  getFeatureStats: (featureKey: string, featureType: EntityType) => {
    return requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.OTHERS,
        entityId: featureKey,
      })
      .get("feature-flags/stats", {
        params: { featureKey, type: featureType.toLowerCase() },
      });
  },

  updateFeatureFlag(params: {
    companyId: string;
    featureKey: string;
    enabled: boolean;
  }) {
    return requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.COMPANY,
        entityId: params.companyId,
      })
      .put(`feature-flags/company/${encodeURIComponent(params.companyId)}`, {
        featureFlags: {
          [params.featureKey]: params.enabled,
        },
      });
  },

  updateFeatureFlagConfig(params: {
    companyId: string;
    featureKey: string;
    locationEnabled?: Record<string, boolean>;
    locationVisibility?: Record<string, boolean>;
    companyEnabled?: boolean;
    companyVisibility?: boolean;
  }) {
    return requests(SERVICE_NAME.LABS_API)
      .log({
        module: Module.LABS,
        entityType: EntityType.COMPANY,
        entityId: params.companyId,
      })
      .put(
        `feature-flags/company/${encodeURIComponent(params.companyId)}/config`,
        {
          featureKey: params.featureKey,
          locationEnabled: params.locationEnabled,
          locationVisibility: params.locationVisibility,
          companyEnabled: params.companyEnabled,
          companyVisibility: params.companyVisibility,
        }
      );
  },
};

export default LabsService;
